import { Dispatch, SetStateAction, useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button, FormHelperText } from "@mui/material";

interface ModalProps {
    open: boolean;
    onClose: () => void;
    setOpen: Dispatch<SetStateAction<boolean>>;
}

export default function SetSPHealthScore({
    open,
    onClose,
    setOpen,
}: ModalProps) {
    const [scores, setScores] = useState({
        bookingFrequency: "",
        completionRate: "",
        cancellations: "",
        customerRatings: "",
    });

    const [error, setError] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setScores({ ...scores, [e.target.name]: e.target.value });
    };

    const validateScores = () => {
        const total = Object.values(scores).reduce((acc, val) => acc + (Number(val) || 0), 0);
        if (total !== 100) {
            setError(`Total score must be 100%. Current total: ${total}%`);
            return false;
        }
        setError(null);
        return true;
    };

    const handleSave = () => {
        if (validateScores()) {
            console.log("Saved SP Scores:", scores);
            setOpen(false);
        }
    };

    // Clear error when inputs change
    useEffect(() => {
        const total = Object.values(scores).reduce((acc, val) => acc + (Number(val) || 0), 0);
        if (total > 100) {
            setError(`Total score exceeds 100%. Current total: ${total}%`);
        } else if (error) {
            setError(null);
        }
    }, [scores]);


    return (
        <Modal
            className="modal rejectReason_modal"
            open={open}
            onClose={onClose}
        >
            <div className="modal-dialog take_action_modal">
                <div className="modal-body">

                    {/* Header */}
                    <div className="modal_title">
                        <h2>Set Service Provider Health Score</h2>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setOpen(false)}
                            aria-label="Close"
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Form */}
                    <div className="form">
                        <div className="gap_p">

                            {/* Booking Frequency */}
                            <div className="control_group w_50">
                                <label htmlFor="sp-booking-freq">Booking Frequency (%)</label>
                                <TextField
                                    name="bookingFrequency"
                                    type="number"
                                    placeholder="0 - 100"
                                    inputProps={{ min: 0, max: 100, id: "sp-booking-freq" }}
                                    fullWidth
                                    hiddenLabel
                                    value={scores.bookingFrequency}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Completion Rate */}
                            <div className="control_group w_50">
                                <label htmlFor="sp-completion-rate">Completion Rate (%)</label>
                                <TextField
                                    name="completionRate"
                                    type="number"
                                    placeholder="0 - 100"
                                    inputProps={{ min: 0, max: 100, id: "sp-completion-rate" }}
                                    fullWidth
                                    hiddenLabel
                                    value={scores.completionRate}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Cancellations */}
                            <div className="control_group w_50">
                                <label htmlFor="sp-cancellations">Cancellations (%)</label>
                                <TextField
                                    name="cancellations"
                                    type="number"
                                    placeholder="0 - 100"
                                    inputProps={{ min: 0, max: 100, id: "sp-cancellations" }}
                                    fullWidth
                                    hiddenLabel
                                    value={scores.cancellations}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Customer Ratings */}
                            <div className="control_group w_50">
                                <label htmlFor="sp-customer-ratings">Customer Ratings (%)</label>
                                <TextField
                                    name="customerRatings"
                                    type="number"
                                    placeholder="0 - 100"
                                    inputProps={{ min: 0, max: 100, id: "sp-customer-ratings" }}
                                    fullWidth
                                    hiddenLabel
                                    value={scores.customerRatings}
                                    onChange={handleChange}
                                />
                            </div>

                            {error && (
                                <div className="control_group w_100">
                                    <FormHelperText error>{error}</FormHelperText>
                                </div>
                            )}

                        </div>

                        {/* Footer */}
                        <div className="footer_btn_flex">
                            <Button
                                className="btn btn_grey"
                                onClick={() => setOpen(false)}
                            >
                                Cancel
                            </Button>

                            <Button
                                className="btn btn_primary"
                                onClick={handleSave}
                            >
                                Save Score
                            </Button>
                        </div>
                    </div>

                </div>
            </div>
        </Modal>
    );
}
